import React, { useState } from 'react';
import { ChevronLeft, Upload, Car } from 'lucide-react';
import type { ConsultancyPersistedVehicle } from '../../lib/consultancyStore';
import { PaymentViaFields, emptyPaymentVia, validatePaymentVia } from '../../components/common/PaymentViaFields';
import { defaultAdminEntryDate, resolveWorkspaceAsOfDate } from '../../lib/workspaceAsOf';

interface SaleProductProps {
  onBack: () => void;
  onSubmit: (data: Record<string, unknown>) => void;
  setActiveItem?: (item: string) => void;
  /** Only these rows can be sold — same list as “Available” in inventory. */
  availableVehicles: ConsultancyPersistedVehicle[];
}

export function SaleProduct({ onBack, onSubmit, setActiveItem, availableVehicles }: SaleProductProps) {
  const [formData, setFormData] = useState({
    vehicleId: '',
    customerName: '',
    customerContact: '',
    buyerInformation: '',
    sellingPrice: '',
    dealRef: '',
    saleDate: defaultAdminEntryDate() || resolveWorkspaceAsOfDate(),
    documents: null as FileList | null,
  });
  const [paymentVia, setPaymentVia] = useState(emptyPaymentVia());

  const selectedVehicle = availableVehicles.find((v) => v.id === formData.vehicleId) || null;
  const saleTotal = Math.max(0, parseFloat(String(formData.sellingPrice || '').replace(/,/g, '')) || 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.vehicleId.trim()) {
      window.alert('Select the vehicle you are selling from your available stock.');
      return;
    }
    const payErr = validatePaymentVia(paymentVia, 'collection', saleTotal);
    if (payErr) {
      window.alert(payErr);
      return;
    }
    onSubmit({
      ...formData,
      paymentVia: paymentVia.paymentVia,
      companyBankId: paymentVia.companyBankId,
      paidBy: paymentVia.paidBy,
      cashAmount: paymentVia.cashAmount,
      bankAmount: paymentVia.bankAmount,
    });
  };

  return (
    <div className="app-page space-y-6">
      <header className="app-page-header">
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <button type="button" onClick={onBack} className="app-btn-secondary inline-flex w-fit shrink-0 items-center gap-2 py-2 pl-3 pr-4 text-sm">
            <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
            Back
          </button>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800">Consultancy · Sale</p>
            <h1 className="app-page-title mt-0.5">Sell from stock</h1>
            <p className="app-page-sub mt-1">Sell a vehicle from your stock.</p>
          </div>
        </div>
      </header>

      {availableVehicles.length === 0 ? (
        <div className="app-card mx-auto max-w-3xl border border-dashed border-amber-200 bg-amber-50/50 p-6 text-center text-sm text-amber-950">
          <Car className="mx-auto h-10 w-10 text-amber-700/80" aria-hidden />
          <p className="mt-3 font-semibold">No available vehicles to sell</p>
          <p className="mt-1 text-amber-900/90">
            Use <strong>Buy · add stock</strong> on the consultancy home screen first. Only inventory you acquired appears
            here for retail delivery.
          </p>
          <button type="button" onClick={onBack} className="app-btn-primary mt-4">
            Back to desk
          </button>
        </div>
      ) : (
        <div className="app-card mx-auto max-w-3xl overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
          <div className="app-card-inner">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="app-label" htmlFor="sale-vehicle">
                  Vehicle from stock (required)
                </label>
                <select
                  id="sale-vehicle"
                  className="app-field"
                  value={formData.vehicleId}
                  onChange={(e) => setFormData({ ...formData, vehicleId: e.target.value })}
                  required
                >
                  <option value="">Select a vehicle…</option>
                  {availableVehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} — {v.model} · ₹{v.price.toLocaleString('en-IN')}
                      {v.location ? ` · ${v.location}` : ''}
                    </option>
                  ))}
                </select>
                {selectedVehicle && (
                  <p className="mt-1.5 text-xs text-zinc-500">
                    Book value ₹{selectedVehicle.price.toLocaleString('en-IN')}
                    {selectedVehicle.location ? ` · ${selectedVehicle.location}` : ''}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
                <div className="md:col-span-2">
                  <label className="app-label" htmlFor="sale-deal-ref">
                    Deal reference (optional)
                  </label>
                  <input
                    id="sale-deal-ref"
                    type="text"
                    className="app-field"
                    placeholder="Finance case / invoice no."
                    value={formData.dealRef}
                    onChange={(e) => setFormData({ ...formData, dealRef: e.target.value })}
                  />
                </div>
                <div>
                  <label className="app-label" htmlFor="sale-customer">
                    Customer name
                  </label>
                  <input
                    id="sale-customer"
                    type="text"
                    className="app-field"
                    placeholder="Registered buyer"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="app-label" htmlFor="sale-contact">
                    Customer contact
                  </label>
                  <input
                    id="sale-contact"
                    type="tel"
                    className="app-field"
                    placeholder="Phone or email"
                    value={formData.customerContact}
                    onChange={(e) => setFormData({ ...formData, customerContact: e.target.value })}
                  />
                </div>
                <div>
                  <label className="app-label" htmlFor="sale-price">
                    Selling price (₹)
                  </label>
                  <input
                    id="sale-price"
                    type="number"
                    min={0}
                    className="app-field"
                    placeholder="OTR / agreed"
                    value={formData.sellingPrice}
                    onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="app-label" htmlFor="sale-date">
                    Sale date
                  </label>
                  <input
                    id="sale-date"
                    type="date"
                    className="app-field"
                    value={formData.saleDate}
                    onChange={(e) => setFormData({ ...formData, saleDate: e.target.value })}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <PaymentViaFields
                    mode="collection"
                    value={paymentVia}
                    onChange={setPaymentVia}
                    totalAmount={saleTotal}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="app-label" htmlFor="sale-notes">
                    Buyer information
                  </label>
                  <textarea
                    id="sale-notes"
                    className="app-field min-h-[5rem] resize-y"
                    placeholder="Co-buyer, delivery address, trade-in notes"
                    rows={3}
                    value={formData.buyerInformation}
                    onChange={(e) => setFormData({ ...formData, buyerInformation: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="app-label">Documents</label>
                  <label className="mt-1 flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-zinc-300 bg-zinc-50/80 px-4 py-8 transition-colors hover:border-emerald-300/80 hover:bg-white">
                    <Upload className="h-8 w-8 text-zinc-400" aria-hidden />
                    <span className="mt-2 text-sm text-zinc-600">Click to upload (demo — not stored)</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => setFormData({ ...formData, documents: e.target.files || null })}
                    />
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 border-t border-zinc-100 pt-6">
                <button type="button" onClick={onBack} className="app-btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="app-btn-primary">
                  Record sale &amp; mark unit sold
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
